/**
 * ONE reading of "why did the voice note fail" (F98, Pavel 2026-08-07).
 *
 * Every recorder surface used to wrap its whole start-and-send path in a
 * single `try { ... } catch { setPhase("error") }` and print ONE sentence.
 * On the sales page that sentence was "Nahravani se nepodarilo spustit.
 * Zkontroluj prosim pristup k mikrofonu." - so a Scribe outage, a dropped
 * connection and a refused note all accused the microphone, while the
 * microphone was demonstrably working (Pavel's screenshots: the error, then
 * the same panel recording at 0:12 with the Chrome mic icon lit).
 *
 * The named-reason law (F44) applies per CAUSE, so the cause has to survive
 * the catch. `classifyStartFailure` reads `error.name` (the browser states
 * the cause there and nowhere else) and `classifySendFailure` separates the
 * three ways a finished recording fails to arrive. Both return a reason, and
 * the reason picks the sentence AND the recovery action: a start failure
 * offers "record again", a send failure offers "send again" because the
 * recording is still held.
 */

export type VoiceFailure =
  // The recording never started.
  | "unsupported"
  | "permission"
  | "busy"
  | "no_device"
  | "mic_failed"
  // The recording exists and is held; only delivery failed.
  | "offline"
  | "transcribe_failed"
  | "send_failed"
  | "rate_limited"
  // F161: the server renewed the session under the page and refused the
  // page's old token. The take is held; the app has already taken a fresh
  // token, so the resend goes through.
  | "session_renewed"
  // The recording arrived but carried no speech.
  | "empty"
  // The take is longer than one transcription request may carry. Resending the
  // same bytes can never work, so this is NOT a held recording.
  | "too_long";

/** Failures that leave a recording worth re-sending. */
const RESENDABLE: ReadonlySet<VoiceFailure> = new Set<VoiceFailure>([
  "offline",
  "transcribe_failed",
  "send_failed",
  // The take is fine and the brake is on the RATE: the same bytes go out
  // again in a moment.
  "rate_limited",
  "session_renewed"
]);

export function voiceFailureKeepsRecording(failure: VoiceFailure): boolean {
  return RESENDABLE.has(failure);
}

function errorName(error: unknown): string {
  if (typeof error === "object" && error !== null && "name" in error) {
    const name = (error as { name?: unknown }).name;
    if (typeof name === "string") return name;
  }
  return "";
}

function errorMessage(error: unknown): string {
  if (typeof error === "object" && error !== null && "message" in error) {
    const message = (error as { message?: unknown }).message;
    if (typeof message === "string") return message;
  }
  return "";
}

/**
 * Why `getUserMedia` / `new MediaRecorder` / `recorder.start()` refused.
 * Names per the Media Capture spec plus the legacy Chrome/Firefox aliases;
 * anything unmapped is an honest "could not start", never a permission claim.
 */
export function classifyStartFailure(error: unknown): VoiceFailure {
  switch (errorName(error)) {
    case "NotAllowedError":
    case "PermissionDeniedError":
    case "SecurityError":
      return "permission";
    // The device exists but something else holds it (another tab, a call
    // app, the OS), or the OS aborted the capture mid-handshake.
    case "NotReadableError":
    case "TrackStartError":
    case "AbortError":
      return "busy";
    case "NotFoundError":
    case "DevicesNotFoundError":
    case "OverconstrainedError":
    case "ConstraintNotSatisfiedError":
      return "no_device";
    // MediaRecorder throws NotSupportedError for a mime it cannot record and
    // TypeError when the constructor arguments do not fit this browser.
    case "NotSupportedError":
    case "TypeError":
      return "unsupported";
    default:
      return "mic_failed";
  }
}

/**
 * Why a finished recording did not arrive. `online` comes from the browser,
 * so a dropped connection is named as one instead of blaming the server.
 */
export function classifySendFailure(error: unknown, online: boolean): VoiceFailure {
  const marker = errorMessage(error);
  // A take with no speech in it is a fact about the recording, not a delivery
  // problem: it is decided BEFORE the offline branch (being offline does not
  // put speech into it) and it is deliberately NOT resendable, so a forgotten
  // open microphone can never be paid for twice.
  if (marker === "no_speech") return "empty";
  // Both are decided BEFORE the offline branch: they are facts the server
  // stated, and being offline afterwards does not change what it said.
  if (marker === "recording_too_large") return "too_long";
  if (marker === "rate_limited") return "rate_limited";
  if (marker === "csrf_stale") return "session_renewed";
  if (!online) return "offline";
  if (marker === "transcribe_failed") return "transcribe_failed";
  if (marker === "offline") return "offline";
  return "send_failed";
}

/** The sentence shown for a reason. One namespace, every recorder surface. */
export function voiceFailureMessageKey(failure: VoiceFailure): string {
  return `voice.failure.${VOICE_FAILURE_SUFFIX[failure]}`;
}

/** The recovery action offered for a reason. */
export function voiceFailureRetryKey(failure: VoiceFailure): string {
  return voiceFailureKeepsRecording(failure) ? "voice.failure.retrySend" : "voice.failure.retryRecord";
}

const VOICE_FAILURE_SUFFIX: Record<VoiceFailure, string> = {
  unsupported: "unsupported",
  permission: "permission",
  busy: "busy",
  no_device: "noDevice",
  mic_failed: "micFailed",
  offline: "offline",
  transcribe_failed: "transcribe",
  send_failed: "send",
  rate_limited: "rateLimited",
  session_renewed: "sessionRenewed",
  empty: "empty",
  too_long: "tooLong"
};

export const VOICE_FAILURES: readonly VoiceFailure[] = Object.keys(VOICE_FAILURE_SUFFIX) as VoiceFailure[];

/**
 * Cost guard, ported from VoiceableInput so every recorder shares it (Pavel
 * 2026-08-07: "aby nekdo nechal zapnute nahravani a odejde pryc, a bude tam
 * ticho, tak abychom nevyplytvali strasne penize prepisem").
 *
 * Two independent brakes: a hard ceiling on length, and a stop after a run of
 * silence. A take that never carried speech is never sent for transcription
 * at all. The analyser is optional: without it the guard fails OPEN (better to
 * transcribe a real note than to drop one because a browser withheld an
 * AudioContext).
 */
export const VOICE_ACTIVITY_RMS = 0.025;
export const VOICE_SILENCE_STOP_MS = 30_000;
export const WALK_MAX_RECORDING_SECONDS = 20 * 60;
/** Below this a webm/mp4 container holds a header and nothing worth paying for. */
export const VOICE_MIN_SPEECH_BYTES = 800;

export interface VoiceActivityGuard {
  /** Call once per second while recording. */
  sample(now: number): "continue" | "silence_stop" | "limit_stop";
  /** Did this take ever carry speech? Fail-open when no analyser exists. */
  hadVoice(): boolean;
  elapsedSeconds(): number;
}

export function createVoiceActivityGuard(options: {
  analyser: { fftSize: number; getByteTimeDomainData(target: Uint8Array): void } | null;
  maxSeconds: number;
  startedAt: number;
}): VoiceActivityGuard {
  let voiced = options.analyser === null;
  let lastVoiceAt = options.startedAt;
  let elapsed = 0;
  return {
    sample(now: number) {
      elapsed = Math.max(0, Math.round((now - options.startedAt) / 1000));
      if (elapsed >= options.maxSeconds) return "limit_stop";
      const rms = readRms(options.analyser);
      if (rms === null) {
        voiced = true;
        lastVoiceAt = now;
        return "continue";
      }
      if (rms > VOICE_ACTIVITY_RMS) {
        voiced = true;
        lastVoiceAt = now;
        return "continue";
      }
      return now - lastVoiceAt > VOICE_SILENCE_STOP_MS ? "silence_stop" : "continue";
    },
    hadVoice: () => voiced,
    elapsedSeconds: () => elapsed
  };
}

function readRms(analyser: { fftSize: number; getByteTimeDomainData(target: Uint8Array): void } | null): number | null {
  if (!analyser) return null;
  const data = new Uint8Array(analyser.fftSize);
  analyser.getByteTimeDomainData(data);
  let sum = 0;
  for (const value of data) {
    const x = (value - 128) / 128;
    sum += x * x;
  }
  return Math.sqrt(sum / data.length);
}

/**
 * Is this take worth paying Scribe for? The two answers a caller needs before
 * spending money: it must have carried speech, and it must hold more than a
 * container header.
 */
export function worthTranscribing(input: { hadVoice: boolean; byteSize: number }): boolean {
  return input.hadVoice && input.byteSize > VOICE_MIN_SPEECH_BYTES;
}
